import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CommunicationTemplateReplacements } from 'src/app/core/models/system/communication/communication-template-replacements';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';

/**
 * Customise Message Component
 * The editable message body panel: insert chips for the system values valid for this message type, reset to default,
 * a character count, and a warning for any @@Placeholder that is not one of them.
 */
//RBW_20261020 - Extracted from provision-modal-form and redeploy-modal-form, which each carried their own copy. The
//               caret handling now uses a ViewChild rather than document.getElementById, because the old lookup found
//               the first matching element on the page and would have inserted into the wrong box once two panels
//               could be open at once.
@Component({
  selector: 'app-customise-message',
  templateUrl: './customise-message.component.html',
  styleUrls: ['./customise-message.component.scss']
})

export class CustomiseMessageComponent {

  @ViewChild('messageTextArea', { static: false }) messageTextArea!: ElementRef<HTMLTextAreaElement>;

  /** Whether the body can be edited at all; false renders it read only. */
  @Input() canEdit: boolean = true;

  /** The wording "Reset to default" restores. Empty hides the link. */
  @Input() defaultText: string = "";

  @Input() expanded: boolean = false;

  @Input() footerText: string = "";

  /** Prefixes the element ids so two panels on one page stay distinct. */
  @Input() idPrefix: string = "customisemessage";

  @Input() label: string = "Message text";

  /** The system values valid for this message type. Anything else typed is reported as unknown. */
  @Input() replacements: CommunicationTemplateReplacements[] = [];

  @Input() requiredText: string = "A message is required";

  @Input() rows: number = 7;

  @Input() title: string = "Customise message";

  @Input() value: string = "";

  @Output() expandedChange = new EventEmitter<boolean>();

  @Output() valueChange = new EventEmitter<string>();

  constructor(
    private exceptionsService: ExceptionsService
  ) { }

  get characters() { return this.text().length; }

  public text(): string {
    return this.value ?? "";
  }

  public onTextChanged(event: Event): void {
    try {
      this.value=(<HTMLTextAreaElement>event.target).value;

      this.valueChange.emit(this.value);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //RBW_20261020 - Any @@word that is not one of this message type's own replacements. A mistyped placeholder is sent
  //               through as literal text, so it has to be caught before the message goes out rather than after.
  //               Static so a host form can judge the body from its own state: a ViewChild on the rendered panel is
  //               only resolved after the first change detection pass, which leaves a submit button stuck disabled.
  public static invalidPlaceHoldersIn(text: string, replacements: CommunicationTemplateReplacements[]): string {
    const validPlaceHolders  =replacements ?? [];
    let   invalidPlaceHolders="";

    //RBW_20261022 - With nothing to check against, every @@value would be reported as unknown. The list is fetched
    //               after the panel renders, so judging it before it arrives flashes a warning at the user about
    //               wording that is perfectly correct.
    if (validPlaceHolders.length==0)
      return "";

    (text ?? "")
      .trim()
      .replace(/\,/," ")
      .replace(/@@/g," @@")
      .split(/\s+/)
      .forEach(word => {
        if (word.startsWith('@@') && !validPlaceHolders.some(x => word.startsWith(x.placeHolder ?? ""))) {
          invalidPlaceHolders+=',' + word;
        }
      });

    return invalidPlaceHolders.replace(/^,/,"");
  }

  public static messageIsValid(text: string, replacements: CommunicationTemplateReplacements[]): boolean {
    return (text ?? "").trim()!="" && CustomiseMessageComponent.invalidPlaceHoldersIn(text, replacements)=="";
  }

  public invalidPlaceHolders(): string {
    try {
      return CustomiseMessageComponent.invalidPlaceHoldersIn(this.text(), this.replacements);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  public isValid(): boolean {
    try {
      return CustomiseMessageComponent.messageIsValid(this.text(), this.replacements);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public warning(): string {
    try {
      if (this.isValid()) {
        return "";
      }

      return this.invalidPlaceHolders()=="" ? this.requiredText : "Unknown system value(s) found";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  public insertReplacement(placeHolder: string): void {
    try {
      const textArea=this.messageTextArea?.nativeElement ?? null;

      //RBW_20260918 - The caret positions belong to the textarea's own value, so the text is spliced from that rather
      //               than from the bound value, keeping the indexes and the string in step.
      const value    =textArea != null ? textArea.value : this.text();
      const start    =textArea?.selectionStart ?? value.length;
      const end      =textArea?.selectionEnd   ?? value.length;
      const scrollTop=textArea?.scrollTop      ?? 0;

      this.value=value.substring(0, start) + placeHolder + value.substring(end);

      this.valueChange.emit(this.value);

      if (textArea != null) {
        const cursorPosition=start + placeHolder.length;

        textArea.focus();

        //RBW_20260828 - Writing the value sends the caret and the scroll position to the end of the textarea, so both
        //               are put back to where the placeholder was inserted.
        setTimeout(() => {
                          textArea.value=this.value;
                          textArea.setSelectionRange(cursorPosition, cursorPosition);
                          textArea.scrollTop=scrollTop;
                        });
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public resetToDefault(): void {
    try {
      this.value=this.defaultText ?? "";

      this.valueChange.emit(this.value);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public toggle(): void {
    try {
      this.expanded=!this.expanded;

      this.expandedChange.emit(this.expanded);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
